import { Auth } from "aws-amplify";
import { datadogLogs } from "@datadog/browser-logs";
import { fetchInvoicesSuccess, fetchInvoicesError, fetchInvoicesStart } from "./invoicesSlice";

// Use environment-specific Go WebSocket endpoint
const GO_WS_API = {
  development: "wss://api-dev.dgiapparel.com/go-server/ws",
  staging: "wss://api-dev.dgiapparel.com/go-server/ws",
  production: "wss://api-prod.dgiapparel.com/go-server/ws",
};

const SOCKET_API =
  import.meta.env.VITE_LOCAL_DEV === "true"
    ? "ws://127.0.0.1:8080/go-server/ws"
    : GO_WS_API[process.env.NODE_ENV] || GO_WS_API.production;

let socket = null;
let socket_id = null;
let reconnectInterval = null;
let heartbeatIntervalId = null; // For periodic heartbeats
let reconnectTimeout = 500;
let messageBuffers = new Map();
let pendingMessages = []; // Add pending messages array
let store = null;

const createSocket = async (user_id) => {
  try {
    // Get current session to retrieve JWT token
    const session = await Auth.currentSession();
    const jwtToken = session.idToken.jwtToken;

    socket_id = `go-socket-${Date.now()}`;
    socket = new WebSocket(
      SOCKET_API + `?user_id=${user_id}&token=${jwtToken}`,
    );
    datadogLogs.logger.info("Created new Go socket instance", { socket_id });
    return socket;
  } catch (error) {
    console.error("Error creating Go WebSocket:", error);
    throw error;
  }
};

const onOpen = () => {
  datadogLogs.logger.info("Go WebSocket connection established", {
    socket_id,
    readyState: socket.readyState,
    pendingMessages: pendingMessages.length,
  });
  // Reset reconnect timeout on successful connection
  reconnectTimeout = 500;

  // Add heartbeat interval
  if (heartbeatIntervalId) {
    clearInterval(heartbeatIntervalId);
  }

  // Start periodic heartbeat every 5 minutes like in dgiApiWebSocketMiddleware
  heartbeatIntervalId = setInterval(
    () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        const heartbeatMsg = {
          action: "heartbeat",
          timestamp: Date.now(),
        };
        socket.send(JSON.stringify(heartbeatMsg));
        console.log("Go WebSocket heartbeat sent:", heartbeatMsg);
      }
    },
    5 * 60 * 1000,
  ); // 5 minutes in ms

  // Send any pending messages once connection is established
  if (pendingMessages.length > 0) {
    console.log(
      `Sending ${pendingMessages.length} pending Go WebSocket messages`,
    );
    const messagesToProcess = [...pendingMessages];
    pendingMessages = [];

    // Process all pending messages with authorization
    messagesToProcess.forEach((message) => {
      sendMessage(message);
    });
  }
};

const onClose = (event) => {
  datadogLogs.logger.info("Go WebSocket connection closed", {
    socket_id,
    code: event.code,
    reason: event.reason,
    wasClean: event.wasClean,
  });

  // Clear the heartbeat interval
  if (heartbeatIntervalId) {
    clearInterval(heartbeatIntervalId);
    heartbeatIntervalId = null;
  }

  if (event.code !== 1000) {
    // Set up reconnection attempt if this wasn't a clean close
    reconnectInterval = setTimeout(() => {
      if (store) {
        const state = store.getState();
        if (state.user && state.user.userCreds) {
          store.dispatch({
            type: "GO_API_WEBSOCKET_CONNECT",
            payload: { user_id: state.user.userCreds.cognitoID },
          });
        }
      }
    }, reconnectTimeout);

    // Exponential backoff for reconnection attempts (max 30 seconds)
    reconnectTimeout = Math.min(reconnectTimeout * 1.5, 30000);
  }
};

const onMessage = (event) => {
  try {
    const message = JSON.parse(event.data);

    // Handle chunked messages
    if (message.chunked === true) {
      console.log("Received chunked message from Go server");
      messageBuffers.set(message.message_id, {
        chunks: new Array(message.total_chunks),
        received: 0,
        totalSize: message.total_size,
        action: message.action,
      });
      return;
    }

    // Handle message chunk
    if (message.chunk_index !== undefined) {
      const buffer = messageBuffers.get(message.message_id);
      if (buffer) {
        // Store chunk
        buffer.chunks[message.chunk_index] = message.data;
        buffer.received++;

        // Check if all chunks received
        if (buffer.received === message.total_chunks) {
          try {
            // Reassemble and parse complete message
            const completeMessage = JSON.parse(buffer.chunks.join(""));

            // Add the original action to the complete message if not present
            if (!completeMessage.action && buffer.action) {
              completeMessage.action = buffer.action;
            }

            // Process the complete message
            handleCompleteMessage(completeMessage);
          } catch (error) {
            console.error("Error processing chunked message:", error);
          } finally {
            // Clean up buffer
            messageBuffers.delete(message.message_id);
          }
        }
      }
      return;
    }

    // Handle regular message
    handleCompleteMessage(message);
  } catch (error) {
    console.error("Error parsing Go WebSocket message:", error);
  }
};

const onError = (error) => {
  datadogLogs.logger.error("Go WebSocket error", { socket_id, error });
};

const handleCompleteMessage = (message) => {
  const { dispatch } = store;

  // Handle error responses
  if (message.error !== undefined) {
    if (message.action === "get_invoices") {
      dispatch(fetchInvoicesError(message.error));
    }
    return;
  }

  // Handle successful invoice responses
  if (message.action === "get_invoices" && message.invoices !== undefined) {
    dispatch(fetchInvoicesSuccess(message.invoices || []));
  }
};

const setupSocketEventHandlers = () => {
  if (!socket) return;

  socket.onopen = onOpen;
  socket.onclose = onClose;
  socket.onmessage = onMessage;
  socket.onerror = onError;
};

const sendMessage = async (message) => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    try {
      // Get the current session and add the access token to the payload for authorization
      const session = await Auth.currentSession();
      const payload = {
        ...message,
        authorization: session.getAccessToken().getJwtToken(),
      };
      socket.send(JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error("Error getting authorization token for message:", error);
      datadogLogs.logger.error(
        "Error adding authorization to Go WebSocket message",
        {
          error: error.message,
          action: message.action,
        },
      );
      // Send the original message as fallback
      socket.send(JSON.stringify(message));
      return true;
    }
  } else {
    console.error("Go WebSocket not connected, cannot send message");
    return false;
  }
};

const goApiWebSocketMiddleware = (storeAPI) => (next) => (action) => {
  // Store reference to the Redux store for later use
  if (!store) {
    store = storeAPI;
  }

  switch (action.type) {
    case "GO_API_WEBSOCKET_CONNECT":
      if (socket) {
        try {
          socket.close();
        } catch (e) {
          console.error("Error closing existing Go WebSocket:", e);
        }
      }

      createSocket(action.payload.user_id)
        .then(() => {
          setupSocketEventHandlers();
        })
        .catch((error) => {
          console.error("Failed to create Go WebSocket:", error);
        });
      break;

    case "GO_API_WEBSOCKET_DISCONNECT":
      if (socket) {
        try {
          socket.close(1000, "Disconnect requested");
        } catch (e) {
          console.error("Error closing Go WebSocket:", e);
          datadogLogs.logger.error("Error closing Go WebSocket:", {
            error: e.message,
            socketId: socket_id,
          });
        }
        socket = null;
      }

      // Clear all intervals and timeouts
      if (reconnectInterval) {
        clearTimeout(reconnectInterval);
        reconnectInterval = null;
      }

      if (heartbeatIntervalId) {
        clearInterval(heartbeatIntervalId);
        heartbeatIntervalId = null;
      }

      // Clean up resources
      messageBuffers.clear();
      pendingMessages = [];
      break;

    case "GO_API_WEBSOCKET_SEND":
      if (action.payload.action === "get_invoices") {
        store.dispatch(fetchInvoicesStart());
      }
      // Add message_id if not present
      if (!action.payload.message_id) {
        action.payload.message_id = `${action.payload.action}-${Date.now()}`;
      }

      if (socket === null || socket.readyState !== WebSocket.OPEN) {
        // If the socket is not open, queue the message
        console.log("Go WebSocket not connected, queueing message for later");
        datadogLogs.logger.info(
          "Queueing Go WebSocket message - socket not ready",
          {
            action: action.payload.action,
            messageId: action.payload.message_id,
            socketId: socket_id,
            socketState: socket?.readyState,
          },
        );
        pendingMessages.push(action.payload);

        // Attempt to reconnect if needed
        const state = storeAPI.getState();
        if (state.user?.user?.UserId) {
          storeAPI.dispatch({
            type: "GO_API_WEBSOCKET_CONNECT",
            payload: { user_id: state.user.user.UserId },
          });
        }
      } else {
        // If connected, send immediately with authorization
        datadogLogs.logger.info("Sending Go WebSocket message", {
          action: action.payload.action,
          messageId: action.payload.message_id,
          socketId: socket_id,
        });
        sendMessage(action.payload);
      }
      break;
  }

  return next(action);
};

export default goApiWebSocketMiddleware;
